import { FC, ReactNode } from 'react';
import { useSiloWeb3 } from '@silo/web3';
import { AssetLogo } from '../icons/AssetLogo';
import { useQueryAssetPriceInUSD } from '../../../../queries/price/hooks';
import { useQueryQuotePrice } from '../../../../queries/price/useQueryQuotePrice';
import { HealthFactor } from '../HealthFactor';
import { DisabledCollateralDescription } from '../../disabled-collateral/DisabledCollateralDescription';
import { borrowLink, depositLink, repayLink, withdrawLink } from '../../../../hooks/useRoute';
import { isUndefined } from '@silo/utils';
import { getRewardsTokenSymbol } from '../../rewards/config';
import { LINKS, makePercent, ProtocolInstance } from '@silo/types';
import { useAssetAddressContext, PositionsPanel as BasePositionsPanel, Positions, BorrowingPowerUsed } from '@silo/ui';

interface PositionsPanelProps {
  positions: Positions;
  title?: ReactNode;
  id?: string;
  protocolInstance: ProtocolInstance;
}

export const PositionsPanel: FC<PositionsPanelProps> = ({ positions, title, id, protocolInstance }) => {
  const rewardsSymbol = getRewardsTokenSymbol(protocolInstance);

  return (
    <>
      <BasePositionsPanel
        positions={positions}
        assetLogo={<PositionsPanelAssetLogo />}
        useAssetPriceInUSD={useQueryAssetPriceInUSD}
        useQuotePrice={useQueryQuotePrice}
        id={id}
        links={{
          disabledBorrowing: LINKS.SILOPEDIA.DISABLED_BORROWING,
          liquidationPrice: LINKS.SILOPEDIA.LIQUIDATION_PRICE,
          getDepositLink: depositLink,
          getBorrowLink: borrowLink,
          getWithdrawLink: withdrawLink,
          getRepayLink: repayLink,
        }}
        healthFactorSlot={<HealthFactor variant="current" healthFactor={positions.healthFactor} />}
        borrowingPowerUsedSlot={<BorrowingPowerUsed borrowingPowerUsed={makePercent(positions.borrowingPowerUsed)} />}
        title={title}
        disabledCollateralDescription={<PositionsPanelDisabledCollateralDescription />}
        rewardsSymbol={rewardsSymbol}
      />
    </>
  );
};

const PositionsPanelDisabledCollateralDescription: FC = () => {
  const assetAddress = useAssetAddressContext();

  return <DisabledCollateralDescription assetAddress={assetAddress} />;
};

const PositionsPanelAssetLogo = () => {
  const { protocolInstance } = useSiloWeb3();
  const assetAddress = useAssetAddressContext();

  if (isUndefined(protocolInstance)) {
    return null;
  }

  return <AssetLogo protocolInstance={protocolInstance} address={assetAddress} size="lg" />;
};
